import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import ReactRouterPropTypes from 'react-router-prop-types';

import { getAllDoctors } from 'api/doctors';
import { getAllTreatingOrgs } from 'api/treating_orgs';
import { updateTreatment, getTreatmentById } from 'api/treatments';

import { REFERRAL_STATUSES, TYPES } from 'config/treatment';
import { BLANK } from 'config/shared';

import ViewComponent from './view_component/index';

import { serialize } from 'components/text_editor/text_editor_config';
import Loading from 'components/loading/loading';

const TreatmentEdit = ({ match, onCountUpdate, onMessages }) => {
  const [loading, setLoading] = useState(false);
  const [treatment, setTreatment] = useState({});
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [treatingOrgOptions, setTreatingOrgOptions] = useState([]);
  const [hasOtherTreatmentType, setHasOtherTreatmentType] = useState(false);
  const [validation, setValidation] = useState({
    doctor: true,
    type: true,
    referralStatus: true,
    treatingOrg: true,
    customer: true,
    referredAt: true,
  });
  const history = useHistory();

  useEffect(() => {
    (async () => {
      setLoading(true);
      const treatment = await getTreatmentById(match.params.id);
      const allDoctors = await getAllDoctors();
      const allTreatingOrgs = await getAllTreatingOrgs();

      const doctorOptions = allDoctors
        .map((d) => ({
          id: d.id,
          value: d.id,
          label: `${d.lastName?.toUpperCase()} ${d.firstName}`,
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label));

      const treatingOrgOptions = allTreatingOrgs.map((d) => ({
        id: d.id,
        value: d.id,
        label: d.name,
      }));
      treatingOrgOptions.unshift(BLANK);

      setDoctorOptions([BLANK, ...doctorOptions]);
      setTreatingOrgOptions(treatingOrgOptions);
      setTreatment({
        ...treatment,
        type: TYPES.find((s) => s.value === treatment.type) || {
          id: treatment.type,
          value: treatment.type,
          label: treatment.type,
        },
        treatingOrg: treatment.treatingOrg
          ? treatingOrgOptions.find((t) => t.id === treatment.treatingOrg.id)
          : BLANK,
        referralStatus: REFERRAL_STATUSES.find(
          (s) => s.value === treatment.referralStatus,
        ),
        doctor: treatment.doctor
          ? doctorOptions.find((d) => d.id === treatment.doctor.id)
          : BLANK,
      });
      setLoading(false);
    })();
  }, [match.params.id]);

  const onSelectChange = (field, selectedOption) => {
    setTreatment({
      ...treatment,
      [field]: selectedOption,
    });

    if (field === 'type' && selectedOption.id === 'other')
      setHasOtherTreatmentType(true);
  };

  const onDateChange = ({ value }) => {
    setTreatment({
      ...treatment,
      referredAt: value,
    });
  };

  const onChange = (e) => {
    const { name, value } = e.target;

    setTreatment({
      ...treatment,
      [name]: value,
    });
  };

  const onMachineSettingsEditorChange = (data) => {
    setTreatment({
      ...treatment,
      machineSettings: data,
    });
  };
  const onNotesEditorChange = (data) => {
    setTreatment({
      ...treatment,
      notes: data,
    });
  };

  const checkValidity = useCallback(() => {
    const validation = {
      doctor: !!treatment.doctor,
      type: !!treatment.type,
      referralStatus: true,
      treatingOrg: true,
      customer: !!treatment.customer,
      referredAt: true, // can be blank or with Date
    };
    setValidation(validation);

    return Object.values(validation).every(Boolean);
  }, [treatment]);

  const onSubmit = useCallback(async () => {
    setLoading(true);

    if (checkValidity()) {
      const {
        meta: { messages },
      } = await updateTreatment(treatment.id, {
        ...treatment,
        type: treatment.type?.value || treatment.type, //respect "other" free text
        treatingOrg:
          typeof treatment.treatingOrg === 'object'
            ? treatment.treatingOrg.value
            : treatment.treatingOrg,
        referralStatus: treatment.referralStatus?.value,
        customer: treatment.customer?.id,
        doctor: treatment.doctor?.id,
        machineSettings: Array.isArray(treatment.machineSettings)
          ? treatment?.machineSettings.map(serialize).join('')
          : treatment.machineSettings,
        notes: Array.isArray(treatment.notes)
          ? treatment?.notes.map(serialize).join('')
          : treatment.notes,
      });

      onMessages(messages);
      onCountUpdate();

      history.goBack();
    }

    setLoading(false);
  }, [checkValidity, history, treatment, onMessages, onCountUpdate]);

  const viewProps = {
    onSubmit,
    onSelectChange,
    onDateChange,
    onChange,
    hasOtherTreatmentType,
    treatment,
    doctorOptions,
    treatingOrgOptions,
    validation,
    onMachineSettingsEditorChange,
    onNotesEditorChange,
  };

  if (loading) return <Loading />;
  return <ViewComponent {...viewProps} />;
};

TreatmentEdit.propTypes = {
  match: ReactRouterPropTypes.match.isRequired,
  onMessages: PropTypes.func.isRequired,
  onCountUpdate: PropTypes.func.isRequired,
};

export default TreatmentEdit;
